import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SimplePageLayout, SimplePageContentWrapper, PlatformLoader } from '@flexinet/ui-components';
import { usePaymentProfiles } from '../hooks/usePaymentProfiles';
import { AiOutlinePlus } from 'react-icons/ai';
import { SelectionPageHeader } from '../components/SelectionPageHeader';
import { SelectionPageNavigation } from '../navigation/SelectionPageNavigation';

export const PaymentProfileSelectionPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { paymentProfiles, loading } = usePaymentProfiles();

    const productId = searchParams.get('productId');
    const priceId = searchParams.get('priceId');
    const returnTo = searchParams.get('returnTo');

    const handleSelectProfile = (profile: any) => {
        const params = new URLSearchParams();
        if (productId) params.set('productId', productId);
        if (priceId) params.set('priceId', priceId);
        if (returnTo) params.set('returnTo', returnTo);
        params.set('providerId', profile.billingProviderId);
        params.set('providerKey', profile.billingProvider?.key || '');
        
        navigate(`/billing/create-subscription?${params.toString()}`);
    };

    const handleAddPaymentMethod = () => {
        const params = new URLSearchParams(searchParams);
        navigate(`/billing/providers?${params.toString()}`);
    };

    if (loading) {
        return <PlatformLoader message="Loading payment methods..." />;
    }

    return (
        <SimplePageLayout
            pageHeader={<SelectionPageHeader onAddPaymentMethod={handleAddPaymentMethod} />}
            pageNavigation={<SelectionPageNavigation />}
        >
            <SimplePageContentWrapper>
                <Stack gap="lg">
                    {paymentProfiles.length > 0 ? (
                        <Stack gap="sm">
                            {paymentProfiles.map((profile) => (
                                <Card key={profile.id} withBorder p="md" style={{ cursor: 'pointer' }}>
                                    <Group justify="space-between" onClick={() => handleSelectProfile(profile)}>
                                        <Stack gap="xs">
                                            <Group gap="sm">
                                                <Text fw={500}>
                                                    {profile.paymentMethodDetails?.card 
                                                        ? `**** **** **** ${profile.paymentMethodDetails.card.last4}` 
                                                        : profile.name || 'Payment Method'}
                                                </Text>
                                                <Badge size="sm" variant="light">
                                                    {profile.billingProvider?.name || profile.billingProviderId}
                                                </Badge>
                                            </Group>
                                            {profile.paymentMethodDetails?.card && (
                                                <Text size="sm" c="dimmed">
                                                    {profile.paymentMethodDetails.card.brand?.toUpperCase()} • 
                                                    Expires {profile.paymentMethodDetails.card.expMonth}/{profile.paymentMethodDetails.card.expYear}
                                                </Text>
                                            )}
                                        </Stack>
                                        <Button>Select</Button>
                                    </Group>
                                </Card>
                            ))}
                        </Stack>
                    ) : (
                        <Card withBorder p="xl">
                            <Stack gap="md" align="center">
                                <Text c="dimmed" ta="center">
                                    No payment methods found. Add one to continue.
                                </Text>
                                <Button
                                    leftSection={<AiOutlinePlus size={16} />}
                                    onClick={handleAddPaymentMethod}
                                >
                                    Add Payment Method
                                </Button>
                            </Stack>
                        </Card>
                    )}
                </Stack>
            </SimplePageContentWrapper>
        </SimplePageLayout>
    );
};
